/**
 * AppConfig – central place for feature flags and tuneable defaults.
 *
 * Values are persisted to / loaded from localStorage so that user
 * preferences survive page refreshes.  Use saveAppConfig(updates) to
 * change one or more values at runtime.
 */

const STORAGE_KEY = 'srmd_appConfig';

const defaults = {
    /** Maximum undo/redo history states (5–100). Default: 25 */
    historySize: 25,

    /** Mirror English timings into the Gujarati VTT on every save. Default: false */
    enableMirroredGujaratiSave: false,

    /** Load actual video audio into the waveform visualiser. Default: false */
    enableWaveforms: false,

    /** Clear transformed dubbing audio files after 24 hours. Default: true */
    clearDubbingAudioFilesIn24Hours: true,

    /** Enable detailed subtitle seek diagnostics. Default: false */
    enableSeekDebug: false,

    /** Capture network diagnostics while seek debug is enabled. Default: true */
    enableSeekDebugNetworkCapture: true,

    /** Minutes of inactivity before an edit session auto-ends. Default: 15 */
    sessionInactivityTimeoutMinutes: 15,
};

// Overlay any previously saved values on top of defaults
const stored = (() => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
})();

const AppConfig = { ...defaults, ...stored };

/**
 * Merge `updates` into AppConfig and persist the result to localStorage.
 * @param {Partial<typeof defaults>} updates
 */
export const saveAppConfig = (updates) => {
    Object.assign(AppConfig, updates);
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(AppConfig));
        window.dispatchEvent(
            new CustomEvent('app-config-updated', {
                detail: { ...AppConfig },
            }),
        );
    } catch {
        // localStorage unavailable – in-memory change still applies
    }
};

export default AppConfig;
